import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Buildings, FileText, UserCircle, SignOut, ChartBar, List, X,
  CurrencyEur, CalendarDots, Briefcase, CaretDown, Check, Gavel, Users, Receipt,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/eigentuemer", label: "Meine Objekte", icon: Buildings, end: true, testid: "owner-nav-properties" },
  { to: "/eigentuemer/kosten", label: "Kosten & Abschläge", icon: CurrencyEur, testid: "owner-nav-costs" },
  { to: "/eigentuemer/kalender", label: "Zahlungskalender", icon: CalendarDots, testid: "owner-nav-calendar" },
  { to: "/eigentuemer/vertraege", label: "Verwalterverträge", icon: Briefcase, testid: "owner-nav-contracts" },
  { to: "/eigentuemer/beschluesse", label: "Beschluss-Sammlung", icon: Gavel, testid: "owner-nav-beschluesse" },
  { to: "/eigentuemer/versammlungen", label: "Versammlungen", icon: Users, testid: "owner-nav-versammlungen" },
  { to: "/eigentuemer/hausgeld", label: "Hausgeld", icon: CurrencyEur, testid: "owner-nav-hausgeld" },
  { to: "/eigentuemer/jahresabrechnung", label: "Jahresabrechnung", icon: Receipt, testid: "owner-nav-jahresabrechnung" },
  { to: "/eigentuemer/dokumente", label: "Dokumente", icon: FileText, testid: "owner-nav-docs" },
  { to: "/eigentuemer/profil", label: "Mein Profil", icon: UserCircle, testid: "owner-nav-profile" },
];

const NAVY = "#0F2A4D";

export default function OwnerPortalLayout() {
  const { user, tenant, logout, refresh: reloadAuth } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [memberships, setMemberships] = useState([]);
  const [activeTid, setActiveTid] = useState(null);
  const [switching, setSwitching] = useState(false);

  // Lade Memberships (für Multi-Tenant-Owner-Switcher)
  useEffect(() => {
    api.get("/auth/memberships")
      .then(({ data }) => {
        setMemberships(data.memberships || []);
        setActiveTid(data.active_tenant_id);
      })
      .catch(() => {});
  }, [user?.id]);

  const switchTenant = async (tid) => {
    if (tid === activeTid || switching) return;
    setSwitching(true);
    try {
      await api.post("/auth/switch-tenant", { tenant_id: tid });
      // Auth-Daten neu laden, dann Hard-Reload für sauberen State
      if (reloadAuth) await reloadAuth();
      toast.success("Mandant gewechselt");
      // Daten der Seite neu laden via reload (alle Cached-States werden invalidiert)
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wechsel fehlgeschlagen");
      setSwitching(false);
    }
  };

  const brandName = tenant?.display_name || tenant?.legal_name || "Domivus";
  const activeMembership = memberships.find((m) => m.tenant_id === activeTid);
  const showSwitcher = memberships.length > 1;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50" data-testid="owner-portal-layout">
      {/* Top Bar – Navy Banking-Look */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-3">
          <Link to="/eigentuemer" className="flex items-center gap-3 min-w-0" data-testid="owner-brand">
            {tenant?.logo_url ? (
              <img src={tenant.logo_url} alt={brandName} className="h-9 w-auto max-w-[180px] object-contain" />
            ) : (
              <>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: NAVY }}>
                  <Buildings size={20} weight="duotone" className="text-white" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold tracking-tight leading-tight truncate text-slate-900">{brandName}</div>
                  <div className="text-[10px] text-slate-500 leading-tight uppercase tracking-[0.14em]">Eigentümerportal</div>
                </div>
              </>
            )}
          </Link>
          <div className="flex items-center gap-2">
            {showSwitcher && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 hidden sm:inline-flex"
                    disabled={switching}
                    data-testid="owner-tenant-switcher"
                  >
                    <Buildings size={14} />
                    <span className="text-xs font-medium truncate max-w-[160px]">
                      {activeMembership?.tenant_name || "Mandant"}
                    </span>
                    <span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">
                      {memberships.length}
                    </span>
                    <CaretDown size={12} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72">
                  <DropdownMenuLabel className="text-xs text-slate-500">
                    Mandant wechseln · {memberships.length} verbunden
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {memberships.map((m) => (
                    <DropdownMenuItem
                      key={m.tenant_id}
                      onClick={() => switchTenant(m.tenant_id)}
                      className="flex items-center gap-2 cursor-pointer"
                      data-testid={`owner-tenant-option-${m.tenant_id}`}
                    >
                      <div
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: m.accent_color || "#0F2A4D" }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{m.tenant_name}</div>
                        <div className="text-[10px] text-slate-500">Rolle: {m.role}</div>
                      </div>
                      {m.is_active && <Check size={14} className="text-emerald-600 shrink-0" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 hover:bg-slate-100 px-2 sm:px-3" data-testid="owner-user-menu">
                  <UserCircle size={22} />
                  <span className="text-sm hidden sm:inline truncate max-w-[180px]">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel className="truncate max-w-[240px] text-xs text-slate-500">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="owner-logout">
                  <SignOut size={16} className="mr-2" /> Abmelden
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button type="button" className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-md hover:bg-slate-100"
              onClick={() => setMenuOpen(!menuOpen)} aria-label="Menü" data-testid="owner-menu-toggle">
              {menuOpen ? <X size={22} /> : <List size={22} />}
            </button>
          </div>
        </div>

        {/* Navigation – Desktop */}
        <div className="max-w-7xl mx-auto px-4 sm:px-8 hidden lg:block">
          <div className="flex gap-1 -mb-px overflow-x-auto">
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-2 px-4 py-3 text-sm transition-colors border-b-2 ${
                      isActive
                        ? "text-slate-900 font-semibold"
                        : "border-transparent text-slate-500 hover:text-slate-900"
                    }`
                  }
                  style={({ isActive }) => isActive ? { borderColor: NAVY } : {}}
                >
                  <Icon size={16} />
                  {n.label}
                </NavLink>
              );
            })}
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <nav className="lg:hidden border-t border-slate-200 bg-white" data-testid="owner-mobile-nav">
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-5 py-3 text-base border-b border-slate-100 ${
                      isActive ? "text-white font-medium" : "text-slate-700 hover:bg-slate-50"
                    }`
                  }
                  style={({ isActive }) => isActive ? { background: NAVY } : {}}
                >
                  <Icon size={20} /> {n.label}
                </NavLink>
              );
            })}
          </nav>
        )}
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-8 py-6 sm:py-10">
        <Outlet />
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 text-[11px] text-slate-500 flex flex-col sm:flex-row sm:justify-between gap-1">
          <span>© 2026 {brandName} · Eigentümerportal</span>
          <span>powered by Domivus · v1.0</span>
        </div>
      </footer>
    </div>
  );
}
